import type { DeckDetails } from "@amzn/ads-magneto-typescript-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Button, Tooltip, IconButton } from "@amzn/storm-ui";
import { cog } from "@amzn/storm-ui-icons";
import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { ACCOUNT_AGNOSTIC_UNAVAILABLE } from "../../../config/constants";
import { LABELS } from "../../../constants/common";
import { Paths } from "../../../constants/paths";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../constants/telemetry";
import { csatFeedbackStore } from "../../../store/csatFeedback";
import { editContentStore } from "../../../store/editContent";
import { FeatureFlags, FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import { globalWorkbenchStore } from "../../../store/globalWorkbench";
import { navigationStore } from "../../../store/navigation";
import { salesforceStore } from "../../../store/salesforce";
import { workbenchStore } from "../../../store/workbench";
import { yourInsightFilterStore } from "../../../store/yourInsightsFilter";
import { TabToDefaultTypeMap } from "../../../types/filters/common";
import {
    YourInsightFilterContentType,
    YourInsightFilterContentSubType,
} from "../../../types/filters/yourInsights";
import { TopNavBarTabs } from "../../../types/navigation";
import {
    getAdvertiserId,
    getNormalizedAdvertiserId,
    isAccountAgnosticId,
} from "../../../utils/permission.utils";
import { logWithTakt } from "../../../utils/telemetry.utils";
import { getSearchParams } from "../../../utils/url.utils";
import {
    getWorkbenchLabel,
    WorkbenchUnit,
} from "../../common/getWorkbenchLabel";
import {
    DivFlexAlignCenter,
    DivFlexSpaceBetween,
} from "../../common/styled.components";
import { ContactUs } from "../../contactUs/ContactUs";
import { GlobalWorkbenchCartButton } from "../../remote/GlobalWorkbenchCartButton";
import { SettingsSidebar } from "../../settings/SettingsSidebar";
import { UploadPitchedContentModal } from "../insights/pitchedContents/UploadPitchedContentModal";

import { TabbedNav } from "./TabbedNav";

const StyledTopNavBarContainer = styled(DivFlexSpaceBetween)`
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
`;

export interface TopNavBarProps {
    hasPlmAccess?: boolean;
}

export const TopNavBar: React.FunctionComponent<TopNavBarProps> = (
    props: TopNavBarProps,
) => {
    const { hasPlmAccess = false } = props;
    const advertiserId = getAdvertiserId();

    const [activeTab, setActiveTab, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.activeTab, state.setActiveTab, state.setIdentifier],
    );
    const [pptxAssets, getTotalSlides] = useStore(workbenchStore, (state) => [
        state.pptxAssets,
        state.getTotalSlides,
    ]);

    const [setTypes] = useStore(discoveryFilterStore, (state) => [
        state.setTypes,
    ]);
    const [showEditModal] = useStore(editContentStore, (state) => [
        state.showEditModal,
    ]);

    const [sfName] = useStore(salesforceStore, (state) => [
        state.sfContext.name,
    ]);

    const [setYourInsightsTypes] = useStore(yourInsightFilterStore, (state) => [
        state.setTypes,
    ]);

    const [showUserPreferencePanel] = useStore(FeatureFlagsStore, (state) => [
        state[FeatureFlags.USER_PREFERENCE_PANEL],
    ]);

    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);

    const hasWorkbenchAssets = pptxAssets.size > 0;

    const handleOnTabChange = (tab: TopNavBarTabs) => {
        if (!advertiserId && tab !== TopNavBarTabs.YOUR_INSIGHT_DECKS) {
            return;
        }

        const { contentType, contentSubType } = TabToDefaultTypeMap[tab];
        setTypes(contentType, contentSubType());
        setActiveTab(tab);
        logWithTakt(TELEMETRY_ID.TOP_NAV_BAR_SELECTION, {
            tab,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.TOP_NAV_BAR_SELECTION,
            PHOENIX_FEATURE.NAVIGATION,
            {
                customData: {
                    tab,
                },
            },
        );
    };

    const navigate = useNavigate();

    const handleCreateInsightClick = () => {
        navigate(`${Paths.WORKBENCH}${getSearchParams()}`);
    };
    const createInsightLabel: string = getWorkbenchLabel(
        LABELS.CREATE_INSIGHT_DECK,
        getTotalSlides(),
        WorkbenchUnit.SLIDE,
    );

    const shouldDisablePlmUpload = isAccountAgnosticId();
    const [setIsEligibleToProvideFeedback] = useStore(
        csatFeedbackStore,
        (state) => [state.setHasUserPerformedActionWorthFeedback],
    );

    return (
        <StyledTopNavBarContainer>
            <TabbedNav
                activeTab={activeTab}
                handleOnTabChange={handleOnTabChange}
            />
            <DivFlexAlignCenter>
                <ContactUs />
                <GlobalWorkbenchCartButton
                    workbenchScope={getNormalizedAdvertiserId()}
                    overrideInsightsHubRedirect={(savedDeckId?: string) => {
                        setActiveTab(TopNavBarTabs.YOUR_INSIGHT_DECKS);
                        setYourInsightsTypes(
                            YourInsightFilterContentType.INSIGHTS,
                            YourInsightFilterContentSubType.WORKBENCH_INSIGHTS,
                        );
                        setIdentifier(savedDeckId);

                        // 2 seconds after the redirect, mark them eligible for csat feedback
                        setTimeout(() => {
                            setIsEligibleToProvideFeedback(true);
                        }, 2000);
                    }}
                    title={sfName && `${sfName} Insights Workbench`}
                    onCartDataLoaded={(cartData: DeckDetails) => {
                        globalWorkbenchStore.getState().setCartData(cartData);
                    }}
                />
                {activeTab === TopNavBarTabs.YOUR_INSIGHT_DECKS &&
                    hasPlmAccess && (
                        <Tooltip
                            nonInteractive={!shouldDisablePlmUpload}
                            inert={!shouldDisablePlmUpload}
                            message={ACCOUNT_AGNOSTIC_UNAVAILABLE}
                            trigger={
                                <span>
                                    <Button
                                        onClick={() => {
                                            showEditModal();
                                        }}
                                        data-testid="top-nav-bar-upload-insight-deck"
                                        disabled={shouldDisablePlmUpload}
                                    >
                                        Upload insight deck
                                    </Button>
                                </span>
                            }
                        />
                    )}
                {showUserPreferencePanel && (
                    <IconButton
                        icon={cog}
                        label="Settings"
                        onClick={() => setIsSettingsOpen(true)}
                        data-testid="top-nav-bar-settings-button"
                        variant="transparent"
                    />
                )}
            </DivFlexAlignCenter>
            {showUserPreferencePanel && (
                <SettingsSidebar
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                />
            )}
            <UploadPitchedContentModal />
        </StyledTopNavBarContainer>
    );
};
